type TypedEventListener<E extends Event> =
  | ((event: E) => void)
  | { handleEvent(event: E): void };

/**
 * A type-safe EventTarget that ensures event names and their corresponding
 * event types are properly typed throughout the event lifecycle.
 *
 * @example
 * type MyEvents = {
 *   'data-received': CustomEvent<{ data: string }>;
 *   'error': CustomEvent<{ message: string }>;
 *   'ready': Event;
 * };
 *
 * class MyEmitter extends TypedEventTarget<MyEvents> {}
 *
 * const emitter = new MyEmitter();
 *
 * // Type-safe event listening
 * emitter.addEventListener('data-received', (event) => {
 *   console.log(event.detail.data); // TypeScript knows this is a string
 * });
 *
 * // Type-safe event dispatching
 * emitter.dispatchEvent(
 *   new CustomEvent('data-received', { detail: { data: 'hello' } })
 * );
 */
export class TypedEventTarget<
  EventMap extends Record<string, Event>,
> extends EventTarget {
  override addEventListener<K extends keyof EventMap & string>(
    type: K,
    listener: TypedEventListener<EventMap[K]> | null,
    options?: boolean | AddEventListenerOptions,
  ): void;
  override addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions,
  ): void {
    super.addEventListener(type, listener, options);
  }

  override removeEventListener<K extends keyof EventMap & string>(
    type: K,
    listener: TypedEventListener<EventMap[K]> | null,
    options?: boolean | EventListenerOptions,
  ): void;
  override removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
  ): void {
    super.removeEventListener(type, listener, options);
  }

  override dispatchEvent<K extends keyof EventMap & string>(
    event: EventMap[K],
  ): boolean;
  override dispatchEvent(event: Event): boolean {
    return super.dispatchEvent(event);
  }
}
